import { Core } from "@faire/design-tokens";
import { Carousel } from "@faire/retailer-visitor-shared/components/Carousel/Carousel";
import { HorizontalScrollCarousel } from "@faire/retailer-visitor-shared/components/Carousel/HorizontalScrollCarousel";
import {
  ContentContainer,
  VanillaBackground,
} from "@faire/retailer-visitor-shared/components/GiftGuide/common";
import { AspectRatioPicture } from "@faire/retailer-visitor-shared/components/Image/AspectRatioPicture";
import { Image } from "@faire/retailer-visitor-shared/components/Image/Image";
import { optimizeImageUrlFillRectangle } from "@faire/web--source/common/images/optimizeImages";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web--source/common/localization/localize";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import React from "react";
import styled from "styled-components";

interface ITile {
  ctaLink: string;
  displayName: React.ReactNode;
  imageUrl: string;
  imageAlt: string;
}

const GIFT_TILES: ITile[] = [
  {
    ctaLink: "/category/For%20the%20Host",
    displayName: (
      <StrictLocalMsg
        // eslint-disable-next-line @faire/no-mid-sentence-linebreaks
        defaultMessage="Gifts for{lineBreak}the Host"
        description={{
          text: "Link to shop 'Gifts for the Host' category",
        }}
        values={DEPRECATED_backwardsCompatibleValues({ lineBreak: <br /> })}
      />
    ),
    imageUrl:
      "https://cdn.faire.com/fastly/d413940e75fc36d403fd5cca8605cd81ae176f7e02f1490bb60a9901ccf9563e.jpeg",
    imageAlt: "Elegant Sink and Hand Soap",
  },
  {
    ctaLink: "/category/For%20New%20Parents",
    displayName: (
      <StrictLocalMsg
        // eslint-disable-next-line @faire/no-mid-sentence-linebreaks
        defaultMessage="Gifts for{lineBreak}New Parents"
        description={{
          text: "Link to shop 'Gifts for New Parents' category",
        }}
        values={DEPRECATED_backwardsCompatibleValues({ lineBreak: <br /> })}
      />
    ),
    imageUrl:
      "https://cdn.faire.com/fastly/7435d0935a0a893abbd66b55dd3273ad9f98f4c9186f40d510e59ceff1d330b0.jpeg",
    imageAlt: "Cheerful Baby Clapping Their Hands",
  },
  {
    ctaLink: "/category/For%20the%20Foodie",
    displayName: (
      <StrictLocalMsg
        // eslint-disable-next-line @faire/no-mid-sentence-linebreaks
        defaultMessage="Gifts for{lineBreak}the Foodie"
        description={{
          text: "Link to shop 'Gifts for the Foodie' category",
        }}
        values={DEPRECATED_backwardsCompatibleValues({ lineBreak: <br /> })}
      />
    ),
    imageUrl:
      "https://cdn.faire.com/fastly/f25cc510bfd10bbe9ec8275acb4945f0309d141b62002f944054dd5d96ea9db8.jpeg",
    imageAlt: "Jar of Cinnamon, Hazelnut & Almond Nut Blend",
  },
  {
    ctaLink: "/category/For%20the%20Coffee%20&%20Tea%20Lover",
    displayName: (
      <StrictLocalMsg
        // eslint-disable-next-line @faire/no-mid-sentence-linebreaks
        defaultMessage="Gifts for the{lineBreak}Coffee Lover"
        description={{
          text: "Link to shop 'Gifts for the Coffee Lover' category",
        }}
        values={DEPRECATED_backwardsCompatibleValues({ lineBreak: <br /> })}
      />
    ),
    imageUrl:
      "https://cdn.faire.com/fastly/ddeb5c72db2507867ffefd657361ef4b8ea034d73167b83674f2c131d25cf202.jpeg",
    imageAlt: "Bags of Coffee",
  },
  {
    ctaLink: "/category/For%20the%20Eco%20Conscious",
    displayName: (
      <StrictLocalMsg
        // eslint-disable-next-line @faire/no-mid-sentence-linebreaks
        defaultMessage="Gifts for the{lineBreak}Eco-Conscious"
        description={{
          text: "Link to shop 'Gifts for the Eco-Conscious' category",
        }}
        values={DEPRECATED_backwardsCompatibleValues({ lineBreak: <br /> })}
      />
    ),
    imageUrl:
      "https://cdn.faire.com/fastly/2ad90c3a83bec195eb2ef510425af621151dcee90352ce1712d1c78fb6144b00.jpeg",
    imageAlt: "Eco-friendly Wooden Toothbrushes",
  },
];

interface IProps {
  leftAligned?: boolean;
  onClick?: (ctaLink: string) => void;
}

export const GiftGuideTopCarousel: React.FC<IProps> = ({
  onClick,
  leftAligned,
}) => {
  const isSSR = useIsSSR();
  const { isMobile, isTabletAndAbove, isDesktopAndAbove } = useViewport();
  const renderTile = (tile: ITile, index: number) => {
    return (
      <Column key={index}>
        <ImageLinkWrapper
          to={tile.ctaLink}
          onClick={() => {
            if (onClick) {
              onClick(tile.ctaLink);
            }
          }}
        >
          <AspectRatioPicture $hideBorder $width="100%" $ratio={220 / 300}>
            <Image
              placeholderColor={Core.text.inverse}
              src={optimizeImageUrlFillRectangle(
                {
                  url: tile.imageUrl,
                },
                isSSR,
                220,
                300
              )}
              alt={tile.imageAlt}
            />
          </AspectRatioPicture>
        </ImageLinkWrapper>
        <Spacer height={isMobile ? "1x" : "2x"} />
        <TileLink
          linkTo={tile.ctaLink}
          variant="tertiary"
          onClick={() => {
            if (onClick) {
              onClick(tile.ctaLink);
            }
          }}
        >
          <Typography
            variant={
              isTabletAndAbove ? "subheadingSansMedium" : "paragraphSansRegular"
            }
          >
            {tile.displayName}
          </Typography>
        </TileLink>
      </Column>
    );
  };

  return (
    <VanillaBackground>
      <StyledContentContainer
        align={isTabletAndAbove && !leftAligned ? "center" : "flex-start"}
      >
        {/* This is an H1 tag for SEO purposes */}
        <Typography
          variant={
            isTabletAndAbove && !leftAligned
              ? "displayMSerifRegular"
              : "displaySSerifRegular"
          }
          as="h1"
        >
          <StrictLocalMsg
            defaultMessage="Faire Gift Guide"
            description={{ text: "Title to the Wholesale Gifts Page" }}
          />
        </Typography>
      </StyledContentContainer>
      <CarouselContainer>
        {isDesktopAndAbove ? (
          <Carousel
            name="seo-gift-page-top-carousel"
            itemsPerSlide={5}
            itemGap={16}
            padItems
            renderItem={renderTile}
            items={GIFT_TILES}
          />
        ) : (
          <HorizontalScrollCarousel
            itemsPerSlide={isMobile ? 2 : 3}
            itemGap={16}
            renderItem={renderTile}
            items={GIFT_TILES}
            peekGapPercent={20}
            lateralPadding={24}
          />
        )}
      </CarouselContainer>
      <Spacer
        height={{
          mobileAndAbove: 48,
          tabletAndAbove: 72,
          desktopAndAbove: 60,
        }}
      />
    </VanillaBackground>
  );
};

export const StyledContentContainer = styled(ContentContainer)`
  padding: ${getSpacing("4x", "3x", "2x")}
    ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${getSpacing("4x", "3x", "4x")};
  }
  ${responsiveMediaQueries.desktopAndAbove} {
    padding: ${getSpacing("7x", "3x", "6x")};
  }
`;

export const CarouselContainer = styled(ContentContainer)`
  ${responsiveMediaQueries.desktop} {
    padding: 0 ${getSpacing("7x")};
  }

  ${responsiveMediaQueries.xLargeAndAbove} {
    padding: 0 136px;
  }
`;

const TileLink = styled(Button)`
  align-self: flex-start;
`;

const ImageLinkWrapper = styled(Link)`
  width: 100%;
`;
